import { useEffect, useState } from "react";

import { api_without_cancellation } from "../../../../api/api";

import "../../../Insurance/AddInsurancePopup.css";

const ENTITY_GROUPS = [
  { type: "Client", label: "Clients", icon: "ic-client" },
  { type: "Defendant", label: "Defendants", icon: "ic-defendants" },
  { type: "Witness", label: "Witnesses", icon: "ic-witnesses" },
  { type: "OtherParty", label: "Other Parties", icon: "ic-parties" },
];

const entityName = (entity) => {
  const firstName = entity.party_first_name || entity.witness_first_name || entity.first_name || "";
  const lastName = entity.party_last_name || entity.witness_last_name || entity.last_name || "";
  return [firstName, lastName].filter(Boolean).join(" ") || entity.entity_name || "Unnamed entity";
};

const withEntitySubtype = (name, subtype) => (subtype ? `${name} - ${subtype}` : name);

const normalizeEntityType = (type) => {
  const key = String(type || "")
    .toLowerCase()
    .replace(/[^a-z]/g, "");
  return {
    client: "Client",
    clients: "Client",
    defendant: "Defendant",
    defendants: "Defendant",
    witness: "Witness",
    witnesses: "Witness",
    otherparty: "OtherParty",
    otherparties: "OtherParty",
    party: "OtherParty",
  }[key];
};

const entityId = (value) => (value && typeof value === "object" ? (value.id ?? value.pk) : value);

const getEntities = (data) => [
  ...(data?.clients || []).map((entity) => ({
    id: entity.id,
    type: "Client",
    name: entityName(entity),
  })),
  ...(data?.defendants || []).map((entity) => ({
    id: entity.id,
    type: "Defendant",
    name: withEntitySubtype(entity.entity_name || entityName(entity), entity.defendantType?.name),
  })),
  ...(data?.witnesses || []).map((entity) => ({
    id: entity.id,
    type: "Witness",
    name: entity.entity_name || entityName(entity),
  })),
  ...(data?.other_parties || []).map((entity) => ({
    id: entity.id,
    type: "OtherParty",
    name: withEntitySubtype(entity.entity_name || entityName(entity), entity.party_type_ID?.name),
  })),
];

export const getPropertyDamageEntity = (propertyDamage) => {
  if (!propertyDamage) return null;

  const entityType =
    normalizeEntityType(propertyDamage.entity_type) ||
    (propertyDamage.defendant_id || propertyDamage.defendant ? "Defendant" : null) ||
    (propertyDamage.party_id || propertyDamage.party || propertyDamage.other_party
      ? "OtherParty"
      : null) ||
    (propertyDamage.witness_id || propertyDamage.witness ? "Witness" : null) ||
    (propertyDamage.client_id || propertyDamage.client || propertyDamage.for_client
      ? "Client"
      : null);
  const relatedEntity = {
    Client: [propertyDamage.client_id, propertyDamage.client, propertyDamage.for_client],
    Defendant: [propertyDamage.defendant_id, propertyDamage.defendant],
    Witness: [propertyDamage.witness_id, propertyDamage.witness],
    OtherParty: [propertyDamage.party_id, propertyDamage.party, propertyDamage.other_party],
  }[entityType];
  const selectedEntityId = [
    propertyDamage.entity_id,
    propertyDamage.entity_obj_id,
    propertyDamage.entity_obj,
    ...(relatedEntity || []),
  ]
    .map(entityId)
    .find((id) => id !== null && id !== undefined && id !== "");

  return selectedEntityId && entityType ? { id: selectedEntityId, type: entityType } : null;
};

export const getPropertyDamageEntityPayload = (selectedEntity) => {
  const fieldByType = {
    Client: "client_id",
    Defendant: "defendant_id",
    Witness: "witness_id",
    OtherParty: "party_id",
  };
  const field = fieldByType[selectedEntity?.type];
  return field && selectedEntity?.id ? { [field]: selectedEntity.id } : {};
};

const PropertyDamageEntityTab = ({ clientId, caseId, selectedEntity, onSelect }) => {
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!clientId || !caseId) return;

    let active = true;
    const fetchEntities = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await api_without_cancellation.get(
          `/api/litigation-page/defendants-and-other-parties/${clientId}/${caseId}/`,
          { params: { litigation_event_popup: true } }
        );
        if (active) setEntities(getEntities(response.data));
      } catch (fetchError) {
        console.error("Unable to load case entities", fetchError);
        if (active) setError("Unable to load case entities. Please try again.");
      } finally {
        if (active) setLoading(false);
      }
    };

    fetchEntities();
    return () => {
      active = false;
    };
  }, [caseId, clientId]);

  if (loading) {
    return <div className="p-3 text-center text-grey">Loading entities…</div>;
  }

  if (error) {
    return <div className="p-3 text-center text-danger">{error}</div>;
  }

  return (
    <div className="m-t-5 m-b-5 p-l-5 p-r-5">
      {ENTITY_GROUPS.map((group) => {
        const groupEntities = entities.filter((entity) => entity.type === group.type);
        if (!groupEntities.length) return null;

        return (
          <div
            key={group.type}
            className={`row mx-auto ps-0 pe-0 align-items-start m-b-5 ${group.type === "Client" ? "" : "m-t-10"}`}
          >
            <div className="col-2 p-r-5 text-start">
              <span className="d-inline-block text-primary-25 text-nowrap">{group.label}</span>
            </div>
            <div className="col-10 ps-0 pe-0">
              <div className="add-insurance-party-grid">
                {groupEntities.map((entity) => {
                  const selected =
                    selectedEntity?.type === entity.type &&
                    String(selectedEntity?.id) === String(entity.id);

                  return (
                    <button
                      type="button"
                      key={`${entity.type}-${entity.id}`}
                      className={`event-type-button height-25 add-insurance-party-button ${selected ? "selected text-white" : "unselected text-primary-25"}`}
                      style={{ fontWeight: "600" }}
                      aria-pressed={selected}
                      onClick={() => onSelect(entity)}
                    >
                      <i className={`ic ic-15 ${group.icon} m-r-5`} />
                      <span className="text-nowrap add-insurance-party-name">{entity.name}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        );
      })}
      {!entities.length && <div className="p-3 text-center text-grey">No case entities found.</div>}
    </div>
  );
};

export default PropertyDamageEntityTab;
